import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { ApiService } from '@app/core/services/api/api.service';
import { AbTestingApiService } from '@app/pages/checkout-app/services/api/abtesting.api.service';
import {
  RoadmapSection,
  StrategyComment,
  ConnectedTest,
  ApiComment,
  ApiStrategy,
  InitResponse,
  mapApiCommentToStrategyComment,
  STRATEGY_CATALOG_BASE_PATH,
  AbTestApiResponse,
} from '../strategy-catalog.models';
import { IUserCollaboration } from '@pdqdev/pdq-shared/dist/interfaces';

@Injectable({ providedIn: 'root' })
export class StrategyOptimizationService {
  private sectionsSubject = new BehaviorSubject<RoadmapSection[]>([]);
  private strategiesCache: ApiStrategy[] = [];
  private commentCountsSubject = new BehaviorSubject<Record<string, number>>({});
  private testStatusMap = new Map<string, string>();
  private initialized = false;

  constructor(
    private apiService: ApiService,
    private abTestingApiService: AbTestingApiService,
  ) {}

  // --- Initialization ---

  initialize(): Observable<void> {
    if (this.initialized) return of(undefined);

    return this.apiService.get(`${STRATEGY_CATALOG_BASE_PATH}/init`).pipe(
      map((response: InitResponse) => {
        const sections: RoadmapSection[] = response.sections.map((s) => ({
          id: s.id,
          name: s.name,
          order: s.order,
          isDefault: s.isDefault,
          strategyIds: s.strategies.map((st) => st.strategyId),
        }));
        this.sectionsSubject.next(sections);

        this.strategiesCache = [...response.sections.flatMap((s) => s.strategies), ...response.unassignedStrategies];

        const countsMap: Record<string, number> = {};
        for (const s of this.strategiesCache) {
          if (s.commentCount) {
            countsMap[s.strategyId] = s.commentCount;
          }
        }
        this.commentCountsSubject.next(countsMap);
      }),
      switchMap(() => this.loadTestStatuses()),
      tap(() => (this.initialized = true)),
    );
  }

  updateTestStatusMap(tests: { testId: string; status: string }[]): void {
    this.testStatusMap.clear();
    for (const test of tests) {
      this.testStatusMap.set(test.testId, test.status);
    }
  }

  // --- Synchronous reads from cache ---

  getSections(): RoadmapSection[] {
    return [...this.sectionsSubject.getValue()].sort((a, b) => a.order - b.order);
  }

  isStrategySaved(strategyId: string): boolean {
    return this.sectionsSubject.getValue().some((s) => s.strategyIds.includes(strategyId));
  }

  getSavedStrategyIds(): Set<string> {
    const ids = new Set<string>();
    for (const section of this.sectionsSubject.getValue()) {
      for (const id of section.strategyIds) {
        ids.add(id);
      }
    }
    return ids;
  }

  getSectionNameForStrategy(strategyId: string): string | null {
    const section = this.sectionsSubject.getValue().find((s) => s.strategyIds.includes(strategyId));
    return section?.name ?? null;
  }

  getCommentCount(strategyId: string): number {
    return this.commentCountsSubject.getValue()[strategyId] ?? 0;
  }

  getConnectedTest(strategyId: string): ConnectedTest | null {
    const strategy = this.strategiesCache.find((s) => s.strategyId === strategyId);
    if (strategy?.testId && strategy?.testName) {
      const status = this.testStatusMap.get(String(strategy.testId));
      return { strategyId, testId: strategy.testId, testName: strategy.testName, status };
    }
    return null;
  }

  // --- Strategy mutations ---

  saveStrategy(
    strategyId: string,
    strategyName: string,
    sectionId?: number,
  ): Observable<Pick<ApiStrategy, 'id' | 'strategyId' | 'strategyName'>> {
    return this.apiService
      .post(`${STRATEGY_CATALOG_BASE_PATH}/strategies`, { strategyId, strategyName, sectionId })
      .pipe(tap(() => this.refreshSections()));
  }

  unsaveStrategy(strategyId: string): Observable<{ success: boolean }> {
    return this.apiService.delete(`${STRATEGY_CATALOG_BASE_PATH}/strategies/${strategyId}`).pipe(
      tap(() => {
        const strategy = this.strategiesCache.find((s) => s.strategyId === strategyId);
        if (strategy) {
          if (strategy.testId) {
            strategy.bookmarked = false;
            strategy.sectionId = null;
          } else {
            this.strategiesCache = this.strategiesCache.filter((s) => s.strategyId !== strategyId);
          }
        }
        const sections = this.sectionsSubject.getValue().map((s) => ({
          ...s,
          strategyIds: s.strategyIds.filter((id) => id !== strategyId),
        }));
        this.sectionsSubject.next(sections);
        this.refreshSections();
      }),
    );
  }

  toggleStrategy(strategyId: string, strategyName: string): Observable<boolean> {
    const sections = this.sectionsSubject.getValue();
    const isSaved = sections.some((s) => s.strategyIds.includes(strategyId));

    if (isSaved) {
      const updated = sections.map((s) => ({
        ...s,
        strategyIds: s.strategyIds.filter((id) => id !== strategyId),
      }));
      this.sectionsSubject.next(updated);

      return this.apiService.delete(`${STRATEGY_CATALOG_BASE_PATH}/strategies/${strategyId}`).pipe(
        tap(() => {
          const strategy = this.strategiesCache.find((s) => s.strategyId === strategyId);
          if (strategy) {
            if (strategy.testId) {
              strategy.bookmarked = false;
              strategy.sectionId = null;
            } else {
              this.strategiesCache = this.strategiesCache.filter((s) => s.strategyId !== strategyId);
            }
          }
        }),
        map(() => false),
        catchError(() => {
          this.sectionsSubject.next(sections);
          return of(true);
        }),
      );
    } else {
      const defaultSection = sections.find((s) => s.isDefault);
      const updated = sections.map((s) => (s.isDefault ? { ...s, strategyIds: [...s.strategyIds, strategyId] } : s));
      this.sectionsSubject.next(updated);

      return this.apiService.post(`${STRATEGY_CATALOG_BASE_PATH}/strategies`, { strategyId, strategyName }).pipe(
        tap((response: Pick<ApiStrategy, 'id' | 'strategyId' | 'strategyName'>) => {
          const existing = this.strategiesCache.find((s) => s.strategyId === strategyId);
          if (existing) {
            existing.bookmarked = true;
            existing.sectionId = defaultSection?.id ?? null;
          } else {
            this.strategiesCache.push({
              id: response.id,
              strategyId,
              strategyName,
              bookmarked: true,
              sectionId: defaultSection?.id ?? null,
              sortOrder: defaultSection?.strategyIds.length ?? 0,
              testId: null,
              testName: null,
            });
          }
        }),
        map(() => true),
        catchError(() => {
          this.sectionsSubject.next(sections);
          return of(false);
        }),
      );
    }
  }

  // --- Sections ---

  createSection(name: string): Observable<RoadmapSection> {
    return this.apiService.post(`${STRATEGY_CATALOG_BASE_PATH}/sections`, { name }).pipe(
      tap((section: RoadmapSection) => {
        const sections = [...this.sectionsSubject.getValue(), section];
        this.sectionsSubject.next(sections);
      }),
    );
  }

  renameSection(sectionId: number, name: string): Observable<void> {
    return this.apiService.put(`${STRATEGY_CATALOG_BASE_PATH}/sections/${sectionId}`, { name }).pipe(
      tap(() => {
        const sections = this.sectionsSubject.getValue().map((s) => (s.id === sectionId ? { ...s, name } : s));
        this.sectionsSubject.next(sections);
      }),
      map(() => undefined),
    );
  }

  deleteSection(sectionId: number): Observable<void> {
    return this.apiService.delete(`${STRATEGY_CATALOG_BASE_PATH}/sections/${sectionId}`).pipe(
      tap(() => {
        const current = this.sectionsSubject.getValue();
        const deleted = current.find((s) => s.id === sectionId);
        const defaultSection = current.find((s) => s.isDefault);
        if (deleted && defaultSection && deleted.id !== defaultSection.id) {
          const updated = current
            .filter((s) => s.id !== sectionId)
            .map((s) =>
              s.id === defaultSection.id ? { ...s, strategyIds: [...s.strategyIds, ...deleted.strategyIds] } : s,
            );
          this.sectionsSubject.next(updated);
        }
        this.refreshSections();
      }),
      map(() => undefined),
    );
  }

  reorderSections(sectionIds: number[]): Observable<void> {
    const sections = this.sectionsSubject.getValue();
    const reordered = sectionIds
      .map((id, index) => {
        const section = sections.find((s) => s.id === id);
        return section ? { ...section, order: index } : null;
      })
      .filter((s): s is RoadmapSection => s !== null);
    this.sectionsSubject.next(reordered);

    return this.apiService.put(`${STRATEGY_CATALOG_BASE_PATH}/sections/reorder`, { sectionIds }).pipe(
      map(() => undefined),
      catchError(() => {
        this.sectionsSubject.next(sections);
        return of(undefined);
      }),
    );
  }

  updateSectionOrder(sectionId: number, strategyIds: string[]): Observable<void> {
    const sections = this.sectionsSubject.getValue().map((s) => (s.id === sectionId ? { ...s, strategyIds } : s));
    this.sectionsSubject.next(sections);

    return this.apiService.put(`${STRATEGY_CATALOG_BASE_PATH}/strategies/order`, { sectionId, strategyIds }).pipe(
      map(() => undefined),
      catchError(() => {
        this.refreshSections();
        return of(undefined);
      }),
    );
  }

  moveStrategyBetweenSections(
    strategyId: string,
    fromSectionId: number,
    toSectionId: number,
    toIndex: number,
  ): Observable<void> {
    const sections = this.sectionsSubject.getValue().map((s) => {
      if (s.id === fromSectionId) {
        return { ...s, strategyIds: s.strategyIds.filter((id) => id !== strategyId) };
      }
      if (s.id === toSectionId) {
        const ids = [...s.strategyIds];
        ids.splice(toIndex, 0, strategyId);
        return { ...s, strategyIds: ids };
      }
      return s;
    });
    this.sectionsSubject.next(sections);

    return this.apiService
      .put(`${STRATEGY_CATALOG_BASE_PATH}/strategies/move`, { strategyId, toSectionId, toIndex })
      .pipe(
        map(() => undefined),
        catchError(() => {
          this.refreshSections();
          return of(undefined);
        }),
      );
  }

  // --- Comments ---

  uploadCommentImages(files: File[]): Observable<string[]> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    return this.apiService
      .postFormData(`${STRATEGY_CATALOG_BASE_PATH}/comments/upload`, formData)
      .pipe(map((response: { keys: string[] }) => response.keys));
  }

  getComments(strategyId: string): Observable<StrategyComment[]> {
    return this.apiService
      .get(`${STRATEGY_CATALOG_BASE_PATH}/comments/${strategyId}`)
      .pipe(map((comments: ApiComment[]) => comments.map(mapApiCommentToStrategyComment)));
  }

  addComment(
    strategyId: string,
    message: string,
    urls?: string[],
    userCollaboration?: IUserCollaboration[],
  ): Observable<StrategyComment> {
    return this.apiService
      .post(`${STRATEGY_CATALOG_BASE_PATH}/comments`, { strategyId, message, urls, userCollaboration })
      .pipe(
        map((comment: ApiComment) => mapApiCommentToStrategyComment(comment)),
        tap(() => {
          const counts = { ...this.commentCountsSubject.getValue() };
          counts[strategyId] = (counts[strategyId] ?? 0) + 1;
          this.commentCountsSubject.next(counts);
        }),
      );
  }

  editComment(commentId: number, message: string): Observable<void> {
    return this.apiService
      .put(`${STRATEGY_CATALOG_BASE_PATH}/comments/${commentId}`, { message })
      .pipe(map(() => undefined));
  }

  deleteComment(commentId: number, strategyId: string): Observable<void> {
    return this.apiService.delete(`${STRATEGY_CATALOG_BASE_PATH}/comments/${commentId}`).pipe(
      tap(() => {
        const counts = { ...this.commentCountsSubject.getValue() };
        counts[strategyId] = Math.max((counts[strategyId] ?? 0) - 1, 0);
        this.commentCountsSubject.next(counts);
      }),
      map(() => undefined),
    );
  }

  // --- Connected Tests ---

  connectTest(
    strategyId: string,
    testId: string,
    testName: string,
    testStatus?: string,
    strategyName?: string,
  ): Observable<void> {
    return this.apiService
      .put(`${STRATEGY_CATALOG_BASE_PATH}/strategies/${strategyId}/connect-test`, { testId, testName, strategyName })
      .pipe(
        tap(() => {
          const strategy = this.strategiesCache.find((s) => s.strategyId === strategyId);
          if (strategy) {
            strategy.testId = testId;
            strategy.testName = testName;
          } else {
            this.strategiesCache.push({
              id: 0,
              strategyId,
              strategyName: strategyName ?? '',
              bookmarked: false,
              sectionId: null,
              sortOrder: 0,
              testId,
              testName,
            });
          }
          if (testStatus) {
            this.testStatusMap.set(testId, testStatus);
          }
        }),
        map(() => undefined),
      );
  }

  disconnectTest(strategyId: string): Observable<void> {
    return this.apiService.delete(`${STRATEGY_CATALOG_BASE_PATH}/strategies/${strategyId}/connect-test`).pipe(
      tap(() => {
        const strategy = this.strategiesCache.find((s) => s.strategyId === strategyId);
        if (strategy) {
          if (strategy.bookmarked) {
            strategy.testId = null;
            strategy.testName = null;
          } else {
            this.strategiesCache = this.strategiesCache.filter((s) => s.strategyId !== strategyId);
          }
        }
      }),
      map(() => undefined),
    );
  }

  // --- Internal ---

  private loadTestStatuses(): Observable<void> {
    const hasTests = this.strategiesCache.some((s) => s.testId);
    if (!hasTests) return of(undefined);

    const params = new HttpParams().set('limit', '100').set('filter', 'ALL');
    return this.abTestingApiService.get('ab-tests', { params }).pipe(
      tap((tests: AbTestApiResponse[]) => {
        this.testStatusMap.clear();
        for (const test of tests) {
          this.testStatusMap.set(String(test.id), this.deriveTestStatus(test.startedAt, test.endedAt));
        }
      }),
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  private deriveTestStatus(startedAt: string | null, endedAt: string | null): string {
    if (startedAt && endedAt) return 'concluded';
    if (startedAt) return 'running';
    return 'draft';
  }

  private refreshSections(): void {
    this.apiService.get(`${STRATEGY_CATALOG_BASE_PATH}/init`).subscribe({
      next: (response: InitResponse) => {
        const sections: RoadmapSection[] = response.sections.map((s) => ({
          id: s.id,
          name: s.name,
          order: s.order,
          isDefault: s.isDefault,
          strategyIds: s.strategies.map((st) => st.strategyId),
        }));
        this.sectionsSubject.next(sections);

        this.strategiesCache = [...response.sections.flatMap((s) => s.strategies), ...response.unassignedStrategies];

        const countsMap: Record<string, number> = {};
        for (const s of this.strategiesCache) {
          if (s.commentCount) {
            countsMap[s.strategyId] = s.commentCount;
          }
        }
        this.commentCountsSubject.next(countsMap);
      },
    });
  }
}
